/**
 * Approvals inbox (ARCHITECTURE.md §8, §5, §9).
 *
 * Orders parked in `PENDING_APPROVAL` await a human decision. Each card shows the
 * exact order intent, the deadline, and what happens on timeout (`cancel` vs
 * `auto-approve`) — the server-side timer is authoritative regardless of whether
 * this page responds (§5).
 *
 * SAFETY (§0.6): the single-use approve `token` is delivered only in the push
 * payload and is deliberately never returned by the approvals API. Approving from
 * the web therefore asks the operator to paste that token (from their push /
 * notification), which authorizes the transition. **Reject** needs no token.
 *
 * The data layer lives in `features/approvals/{api,hooks}` (ARCHITECTURE-LAYERS.md
 * §4.1) — this page is the component only.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge, cn, formatDecimal, formatMoney } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import type { Approval, OrderIntent } from "@richrich/contracts";
import { PageShell } from "../components/PageShell";
import { Button, EmptyState, ErrorNote, TextField } from "../components/ui/controls";
import { pendingApprovalsQuery } from "../features/approvals/api";
import { useApprove, useReject } from "../features/approvals/hooks";

export const Route = createFileRoute("/approvals")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(pendingApprovalsQuery);
  },
  component: ApprovalsPage,
});

// ── page ─────────────────────────────────────────────────────────────────────

/** A 1-second clock so each card's countdown ticks down live. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function ApprovalsPage() {
  const { t } = useI18n();
  const query = useQuery(pendingApprovalsQuery);
  const now = useNow();
  const approvals = query.data?.approvals ?? [];

  return (
    <PageShell
      eyebrow={t("approvals.eyebrow")}
      title={t("approvals.title")}
      description={t("approvals.description")}
      loading={query.isLoading}
    >
      {query.isError ? (
        <ErrorNote>{t("approvals.loadError")}</ErrorNote>
      ) : approvals.length === 0 ? (
        <EmptyState
          title={t("approvals.emptyTitle")}
          description={t("approvals.emptyDescription")}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {approvals.map((approval) => (
            <ApprovalCard key={approval.id} approval={approval} now={now} />
          ))}
        </div>
      )}
    </PageShell>
  );
}

type TFunc = ReturnType<typeof useI18n>["t"];

function remainingLabel(
  expiresAt: number,
  now: number,
  t: TFunc,
): { text: string; urgent: boolean } {
  const ms = expiresAt - now;
  if (ms <= 0) return { text: t("approvals.expired"), urgent: true };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const dur =
    h > 0
      ? t("approvals.durationHm", { h, m })
      : m > 0
        ? t("approvals.durationMs", { m, s })
        : t("approvals.durationS", { s });
  return { text: t("approvals.timeLeft", { time: dur }), urgent: ms < 60_000 };
}

function ApprovalCard({ approval, now }: { approval: Approval; now: number }) {
  const { t } = useI18n();
  const approve = useApprove();
  const reject = useReject();
  const [showApprove, setShowApprove] = useState(false);
  const [token, setToken] = useState("");

  const countdown = remainingLabel(approval.expiresAt, now, t);
  const busy = approve.isPending || reject.isPending;

  return (
    <div className="rounded-panel border border-hairline bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <OrderIntentSummary intent={approval.orderIntent} />
        <div className="flex flex-col items-end gap-1">
          <Badge tone={countdown.urgent ? "critical" : "warning"} dot>
            {countdown.text}
          </Badge>
          <span className="text-xs text-muted">
            {t("approvals.onTimeoutLabel")}{" "}
            <span className="text-ink-secondary">
              {approval.onTimeout === "auto-approve"
                ? t("approvals.autoApprove")
                : t("approvals.cancelTimeout")}
            </span>
          </span>
        </div>
      </div>

      {approval.orderIntent.confirmHighValue ? (
        <p className="mt-3 rounded-md border border-serious/30 bg-serious/5 px-3 py-2 text-xs text-serious">
          {t("approvals.highValueNote")}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
        {showApprove ? (
          <form
            className="flex w-full flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (token.trim().length > 0) approve.mutate({ id: approval.id, token: token.trim() });
            }}
          >
            <div className="flex-1">
              <TextField
                label={t("approvals.tokenLabel")}
                mono
                autoFocus
                value={token}
                onValueChange={setToken}
                placeholder={t("approvals.tokenPlaceholder")}
                hint={t("approvals.tokenHint")}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                pending={approve.isPending}
                disabled={token.trim().length === 0}
              >
                {t("approvals.confirmApprove")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowApprove(false);
                  setToken("");
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <Button variant="primary" disabled={busy} onClick={() => setShowApprove(true)}>
              {t("approvals.approve")}
            </Button>
            <Button
              variant="danger"
              pending={reject.isPending}
              disabled={approve.isPending}
              onClick={() => reject.mutate({ id: approval.id })}
            >
              {t("approvals.reject")}
            </Button>
          </>
        )}
      </div>

      {approve.isError ? (
        <p className="mt-2">
          <ErrorNote>{t("approvals.tokenRejected")}</ErrorNote>
        </p>
      ) : null}
      {reject.isError ? (
        <p className="mt-2">
          <ErrorNote>{t("approvals.rejectError")}</ErrorNote>
        </p>
      ) : null}
    </div>
  );
}

function OrderIntentSummary({ intent }: { intent: OrderIntent }) {
  const { t } = useI18n();
  const size =
    intent.qty !== undefined
      ? t("approvals.shares", {
          qty: formatDecimal(intent.qty, { fractionDigits: fracLen(intent.qty) }),
        })
      : intent.orderAmount !== undefined
        ? formatMoney(intent.orderAmount, intent.currency)
        : "—";
  const typeLabel =
    intent.orderType === "MARKET"
      ? t("approvals.typeMarket")
      : intent.orderType === "LIMIT"
        ? t("approvals.typeLimit")
        : intent.orderType;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "font-mono text-sm font-semibold",
            intent.side === "BUY" ? "text-good-text" : "text-critical",
          )}
        >
          {intent.side === "BUY" ? t("approvals.sideBuy") : t("approvals.sideSell")}
        </span>
        <span className="font-mono text-sm font-medium text-ink">{intent.symbol}</span>
        {intent.marketCountry !== undefined ? (
          <span className="font-mono text-[10px] uppercase text-muted">{intent.marketCountry}</span>
        ) : null}
      </div>
      <div className="mt-1 text-sm text-ink-secondary">
        {typeLabel}
        {intent.timeInForce !== undefined ? ` · ${intent.timeInForce}` : ""} · {size}
        {intent.price !== undefined ? ` @ ${formatMoney(intent.price, intent.currency)}` : ""}
      </div>
    </div>
  );
}

function fracLen(value: string): number {
  const dot = value.indexOf(".");
  return dot === -1 ? 0 : value.length - dot - 1;
}
